import { useEffect, useState } from "react";
import { Cloud, Sun, CloudRain, CloudSnow, CloudDrizzle, X } from "lucide-react";
import api from "@/lib/api";
import { IDS } from "@/constants/testIds";

const iconFor = (code) => {
  if (code == null) return Sun;
  if (code <= 1) return Sun;
  if (code >= 51 && code <= 67) return CloudDrizzle;
  if (code >= 71 && code <= 77) return CloudSnow;
  if (code >= 80 && code <= 99) return CloudRain;
  return Cloud;
};

export default function WeatherStrip({ weather, city, lat, lon }) {
  const [open, setOpen] = useState(false);
  const [hourly, setHourly] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || hourly || lat == null || lon == null) return;
    setLoading(true);
    api.get("/weather/hourly", { params: { lat, lon, hours: 12 } })
      .then((r) => setHourly(r.data.hourly))
      .catch((e) => console.error("hourly failed", e))
      .finally(() => setLoading(false));
  }, [open, lat, lon, hourly]);

  if (!weather) return null;
  const Icon = iconFor(weather.code);
  const hi = weather.temp_max_c != null ? Math.round(weather.temp_max_c) : null;
  const lo = weather.temp_min_c != null ? Math.round(weather.temp_min_c) : null;
  const feels = weather.feels_c != null ? Math.round(weather.feels_c) : null;

  return (
    <>
      <button
        data-testid={IDS.weatherStrip}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/70 backdrop-blur-sm border border-stone-200/60 shadow-[0_2px_8px_rgba(28,25,23,0.04)] hover:bg-white transition-colors active:scale-[0.98]"
      >
        <Icon size={16} strokeWidth={1.5} className="text-[#6B7A54]" />
        <span className="text-sm font-[Manrope] font-medium text-stone-700">
          {city ? `${city} · ` : ""}
          {weather.temp_c != null ? `${Math.round(weather.temp_c)}°C` : ""}
        </span>
        <span className="text-xs font-[Manrope] text-stone-500 capitalize">
          {weather.summary}
        </span>
        {hi != null && lo != null && (
          <span className="text-xs font-[Manrope] text-stone-500">· H {hi}° / L {lo}°</span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-50 flex items-end justify-center"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md bg-[#FBF9F6] rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] font-[Manrope] font-bold tracking-[0.25em] uppercase text-[#6B7A54]">Today</div>
                <h3 className="font-[Cormorant_Garamond] text-3xl italic text-stone-900 leading-tight mt-1">
                  {city || "Your area"}
                </h3>
              </div>
              <button onClick={() => setOpen(false)} className="h-9 w-9 rounded-full border border-stone-200 flex items-center justify-center">
                <X size={16} />
              </button>
            </div>

            <div className="flex items-baseline gap-3 mb-4">
              <div className="font-[Cormorant_Garamond] text-6xl text-stone-900">
                {weather.temp_c != null ? Math.round(weather.temp_c) : "—"}°
              </div>
              <div className="text-sm font-[Manrope] text-stone-600 capitalize">{weather.summary}</div>
            </div>
            <div className="flex gap-4 text-xs font-[Manrope] text-stone-600 mb-6">
              {feels != null && <span>Feels {feels}°C</span>}
              {weather.humidity != null && <span>Humidity {weather.humidity}%</span>}
              {hi != null && lo != null && <span>H {hi}° · L {lo}°</span>}
            </div>

            <div className="text-[10px] font-[Manrope] font-bold tracking-[0.25em] uppercase text-stone-500 mb-2">Next 12 hours</div>
            {loading && <div className="text-xs text-stone-400">Loading…</div>}
            {hourly && (
              <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-6 px-6 pb-2">
                {hourly.map((h, i) => {
                  const HIcon = iconFor(h.code);
                  const t = new Date(h.time);
                  const label = t.getHours() === new Date().getHours() && i === 0 ? "Now" : `${t.getHours()}:00`;
                  return (
                    <div key={h.time} className="shrink-0 w-16 flex flex-col items-center gap-1 py-2">
                      <div className="text-[10px] font-[Manrope] text-stone-500">{label}</div>
                      <HIcon size={16} strokeWidth={1.5} className="text-[#6B7A54]" />
                      <div className="text-sm font-[Manrope] font-medium text-stone-800">
                        {h.temp_c != null ? Math.round(h.temp_c) : "—"}°
                      </div>
                      {h.precip_pct > 20 && (
                        <div className="text-[9px] text-blue-500 font-[Manrope]">{h.precip_pct}%</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
